// ─────────────────────────────────────────────────────────────────────────────
// useRaceSplits — Capture every 500m boundary crossing during a race.
//
// Each crossing records:
//   { boundary_m, elapsed_ms, split_ms, split_500_pace }
// where:
//   • boundary_m       = the 500m mark (500, 1000, 1500, …)
//   • elapsed_ms       = total elapsed time (ms) when that mark was crossed
//   • split_ms         = duration since previous boundary (i.e. time for that 500m)
//   • split_500_pace   = same as split_ms / 500 × 500 = split_ms (kept explicit)
//
// Inputs:
//   active            — only captures while true (race workout running)
//   distance          — current meters rowed (live)
//   elapsedSeconds    — current elapsed time (seconds)
//   target            — race distance (meters)
//
// Returns:
//   splits            — array of crossings (most recent last)
//   reset()           — clears the buffer (call on new race start)
// ─────────────────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useRef, useState } from 'react';

const SPLIT_INCREMENT_M = 500;

export default function useRaceSplits({ active, distance, elapsedSeconds, target }) {
  const [splits, setSplits] = useState([]);
  const lastBoundaryRef = useRef(0); // last boundary already captured

  const reset = useCallback(() => {
    lastBoundaryRef.current = 0;
    setSplits([]);
  }, []);

  // Reset whenever active flips off (race ended / cancelled)
  useEffect(() => {
    if (!active) reset();
  }, [active, reset]);

  useEffect(() => {
    if (!active || typeof target !== 'number' || target <= 0) return;
    const d = Math.floor(distance || 0);
    const elapsedMs = Math.floor((elapsedSeconds || 0) * 1000);
    // Capture every 500m boundary already passed but not yet recorded.
    while (
      lastBoundaryRef.current + SPLIT_INCREMENT_M <= d &&
      lastBoundaryRef.current + SPLIT_INCREMENT_M <= target
    ) {
      const boundary = lastBoundaryRef.current + SPLIT_INCREMENT_M;
      lastBoundaryRef.current = boundary;
      setSplits(prev => {
        const prevElapsed = prev.length > 0 ? prev[prev.length - 1].elapsed_ms : 0;
        const split_ms = elapsedMs - prevElapsed;
        return [...prev, {
          boundary_m: boundary,
          elapsed_ms: elapsedMs,
          split_ms,
        }];
      });
    }
  }, [active, distance, elapsedSeconds, target]);

  return { splits, reset };
}
